import { useLanguage } from "../../contexts/LanguageContext"
import { useDarkMode } from "../../hooks/useDarkMode"

export default function FeatureFlagToggle({ name, label, description, checked, onChange, disabled = false }) {
  const { t, isRTL } = useLanguage()
  const { darkMode }  = useDarkMode()

  // RTL fix: thumb must slide toward start of reading direction
  // LTR: unchecked = translate-x-0, checked = translate-x-5
  // RTL: unchecked = translate-x-0, checked = -translate-x-5
  const thumbTranslate = checked
    ? isRTL ? "-translate-x-5" : "translate-x-5"
    : "translate-x-0"

  return (
    <div className={`border rounded-xl p-4 transition-colors
      ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"}
      ${disabled ? "opacity-50" : ""}
    `}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className={`flex items-center gap-3 mb-1 ${isRTL ? "flex-row-reverse justify-end" : ""}`}>

            {/* Toggle switch */}
            <button
              type="button"
              role="switch"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => !disabled && onChange(name, !checked)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent
                transition-colors duration-200 ease-in-out focus:outline-none
                ${checked ? "bg-blue-600" : darkMode ? "bg-gray-600" : "bg-gray-300"}
                ${disabled ? "cursor-not-allowed" : "cursor-pointer"}
              `}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0
                  transition duration-200 ease-in-out ${thumbTranslate}
                `}
              />
            </button>

            {/* Label */}
            <label
              onClick={() => !disabled && onChange(name, !checked)}
              className={`font-semibold text-sm
                ${darkMode ? "text-white" : "text-gray-900"}
                ${disabled ? "cursor-not-allowed" : "cursor-pointer"}
                ${isRTL ? "text-right" : "text-left"}
              `}
            >
              {label}
            </label>
          </div>

          {/* Description */}
          {description && (
            <p className={`text-xs mt-1 ${isRTL ? "text-right" : "text-left"} ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              {description}
            </p>
          )}
        </div>

        {/* Status badge */}
        <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-full font-medium
          ${checked
            ? "bg-green-500/20 text-green-400"
            : darkMode ? "bg-gray-600/50 text-gray-400" : "bg-gray-200 text-gray-500"
          }
        `}>
          {checked ? t("feature_enabled") : t("feature_disabled")}
        </span>
      </div>
    </div>
  )
}